import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { EffectiveReportAreaScope } from './reportAreaScope';
import {
  MAX_ASSET_RADIUS_KM,
  MIN_ASSET_RADIUS_KM,
  parseAssetRadiusKm,
} from 'components/Reports/utils/buildDraftSitrepPayload';
import { useAorText } from 'utils/aorTerminology';

export type ReportAreaKind = 'incident' | 'aor' | 'asset' | 'custom';

type ReportAreaScopeBarProps = {
  defaultScope: EffectiveReportAreaScope | null;
  customScope: unknown;
  isEditingArea: boolean;
  /** Incident reports edit and reset the incident location, not the linked AOR. */
  areaKind?: ReportAreaKind;
  /** Radius of a point asset's area; null for assets drawn as shapes. */
  assetRadiusKm?: number | null;
  onAssetRadiusChange?: (radiusKm: number) => void;
  disabled?: boolean;
  /** Rendered before the area controls (e.g. Draft status). */
  leading?: React.ReactNode;
  /** Rendered after the status badge (e.g. Edit before Approval). */
  trailing?: React.ReactNode;
  onEditOnMap: () => void;
  onCancelDraw: () => void;
  onResetScope: () => void;
};

const AssetRadiusInput: React.FC<{
  radiusKm: number;
  onCommit: (radiusKm: number) => void;
}> = ({ radiusKm, onCommit }) => {
  const [draft, setDraft] = useState(String(radiusKm));
  const parsed = parseAssetRadiusKm(draft);
  const invalid = parsed === null;

  const commit = () => {
    if (parsed === null) {
      setDraft(String(radiusKm));
      return;
    }
    if (parsed !== radiusKm) onCommit(parsed);
  };

  return (
    <label className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground">
      Radius
      <Input
        type="number"
        inputMode="decimal"
        min={MIN_ASSET_RADIUS_KM}
        max={MAX_ASSET_RADIUS_KM}
        step={1}
        value={draft}
        aria-invalid={invalid || undefined}
        title={`${MIN_ASSET_RADIUS_KM}–${MAX_ASSET_RADIUS_KM} km`}
        className="tw:h-8 tw:w-20"
        data-testid="report-asset-radius"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
      km
    </label>
  );
};

const ReportAreaScopeBar: React.FC<ReportAreaScopeBarProps> = ({
  defaultScope,
  customScope,
  isEditingArea,
  areaKind = 'aor',
  assetRadiusKm = null,
  onAssetRadiusChange,
  disabled = false,
  leading,
  trailing,
  onEditOnMap,
  onCancelDraw,
  onResetScope,
}) => {
  const aorText = useAorText();
  if (disabled && !leading && !trailing) return null;

  const labels = {
    incident: {
      edit: 'Edit Incident Location',
      cancel: 'Cancel Incident Location Edit',
      reset: 'Reset to Incident',
    },
    aor: {
      edit: aorText('Edit AOR on Map'),
      cancel: aorText('Cancel AOR Edit'),
      reset: aorText('Reset to AOR'),
    },
    asset: {
      edit: 'Edit Area on Map',
      cancel: 'Cancel Area Edit',
      reset: 'Reset to Asset Area',
    },
    custom: {
      edit: 'Redraw Area on Map',
      cancel: 'Cancel Redraw',
      reset: null,
    },
  }[areaKind];

  // An edited asset area can always be rebuilt from the asset on the server.
  const canReset = areaKind === 'asset' || Boolean(defaultScope);

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      {leading}
      {trailing}
      {!disabled ? (
        <>
          {isEditingArea ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive tw:active:bg-destructive/10 tw:active:text-destructive"
              title={labels.cancel}
              onClick={onCancelDraw}
            >
              {labels.cancel}
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEditOnMap}
            >
              {labels.edit}
            </Button>
          )}
          {customScope && labels.reset ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canReset}
              onClick={onResetScope}
            >
              {labels.reset}
            </Button>
          ) : null}
          {areaKind === 'asset' &&
          assetRadiusKm !== null &&
          !customScope &&
          onAssetRadiusChange ? (
            <AssetRadiusInput
              key={assetRadiusKm}
              radiusKm={assetRadiusKm}
              onCommit={onAssetRadiusChange}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ReportAreaScopeBar;
